/**
 * Notification router — partner-app FCM tap-to-route logic.
 *
 * Three delivery surfaces handled:
 *
 *   1. Foreground (app is open + visible):
 *      Firebase's onMessage fires while a page is active. The OS
 *      doesn't show a banner in this state, so we render an in-app
 *      toast that the astrologer can tap to route. See
 *      showForegroundNotification() below.
 *
 *   2. Background tap (app open in another tab, or PWA installed and
 *      backgrounded):
 *      The service worker (public/firebase-messaging-sw.js) shows the
 *      OS banner. On click, it reads `notification.data.url` (or the
 *      webpush.fcmOptions.link) and either focuses an existing tab
 *      and navigates, or opens a new window. No client-side JS here.
 *
 *   3. Quit-state tap:
 *      Same as background — the SW handles it because it's
 *      registered. No client-side JS needed.
 *
 * The server (sockets/src/utils/notification.ts) populates a `url`
 * field on every notification payload, derived from `data.category`.
 * This client-side router maps that same category to a react-router
 * navigation when the app is in the foreground.
 *
 * Categories handled here mirror the server-side deriveWebUrl():
 *   APPOINTMENT_REMINDER_ASTROLOGER → details page (or list fallback)
 *   APPOINTMENT_BOOKED_ASTROLOGER   → details page (or list fallback)
 *   MEETING_CANCELLED_ASTROLOGER    → details page (or list fallback)
 *   MEETING_RESCHEDULED_ASTROLOGER  → details page (or list fallback)
 *   BOOKING_CONFIRMATION            → details page (legacy alias)
 *   INSTANT_CALL_REQUEST            → dashboard (modal-driven)
 *   CHAT_REQUEST                    → chat inbox
 *   CALL_NAVIGATION                 → unified call route
 *   (default)                       → dashboard
 */

import toast from "react-hot-toast";

/**
 * Resolve a partner-app URL from a notification payload's data.
 * Prefers an explicit `data.url` set by the server; falls back to
 * category-based routing if `url` is missing or `"/"`.
 *
 * Keep this in sync with sockets/src/utils/notification.ts ::
 * deriveWebUrl — same category → same URL on both sides.
 */
export function urlFromNotification(data) {
  const explicit = data?.url ? String(data.url) : "";
  if (explicit && explicit !== "/") return explicit;

  const category = String(data?.category || "");
  const meetingId = data?.meetingId ? String(data.meetingId) : "";

  switch (category) {
    case "APPOINTMENT_REMINDER_ASTROLOGER":
    case "APPOINTMENT_BOOKED_ASTROLOGER":
    case "MEETING_CANCELLED_ASTROLOGER":
    case "MEETING_RESCHEDULED_ASTROLOGER":
    case "BOOKING_CONFIRMATION":
      return meetingId
        ? `/schedule/appointments/details/${meetingId}`
        : "/schedule/appointments";

    case "INSTANT_CALL_REQUEST":
      return "/";

    case "CHAT_REQUEST":
      return "/interact/chat";

    case "CALL_NAVIGATION":
      return data?.callback ? `/calls/${data.callback}` : "/";

    default:
      return "/";
  }
}

/**
 * Compose a short pre-title and accent color from the category. Used
 * to give each toast a category-specific look (different from generic
 * notifications) so astrologers learn to recognise reminder vs chat
 * vs booking pings at a glance.
 */
function visualForCategory(category) {
  switch (category) {
    case "APPOINTMENT_REMINDER_ASTROLOGER":
      return { kicker: "Starting soon", accent: "#f16631" }; // saffron
    case "APPOINTMENT_BOOKED_ASTROLOGER":
    case "BOOKING_CONFIRMATION":
      return { kicker: "New booking", accent: "#16a34a" }; // green
    case "MEETING_CANCELLED_ASTROLOGER":
      return { kicker: "Cancelled", accent: "#b85c2c" }; // brick
    case "MEETING_RESCHEDULED_ASTROLOGER":
      return { kicker: "Rescheduled", accent: "#7c3aed" }; // violet
    case "CHAT_REQUEST":
      return { kicker: "Chat request", accent: "#2563eb" }; // blue
    case "CALL_NAVIGATION":
      return { kicker: "Consultation", accent: "#f16631" };
    default:
      return { kicker: "Notification", accent: "#6b7280" }; // gray
  }
}

/**
 * Render a toast for a foreground-delivered FCM message. The toast is
 * tappable — clicking routes the user to the same URL the SW would
 * have opened on background tap.
 *
 * Skipped (silent) for INSTANT_CALL_REQUEST because the InstantCall
 * modal already surfaces those via socket state. Doubling up would
 * be noisy.
 *
 * @param {object} payload  Raw firebase/messaging onMessage payload
 * @param {(url: string) => void} navigate  react-router navigate fn
 */
export function showForegroundNotification(payload, navigate) {
  const data = payload?.data || {};
  const notification = payload?.notification || {};

  const category = String(data?.category || "");
  if (category === "INSTANT_CALL_REQUEST") {
    // The InstantCallNotification modal handles this via socket state.
    // Showing a toast on top would be redundant and split the user's
    // attention between two surfaces for the same event.
    return;
  }

  const title = notification.title || data.title || "New notification";
  const body = notification.body || data.body || "";
  const url = urlFromNotification(data);
  const { kicker, accent } = visualForCategory(category);

  // Use react-hot-toast's custom render so we get a real onClick on
  // the toast element. Toast auto-dismisses after `duration` ms; the
  // user can also tap-to-close.
  toast.custom(
    (t) => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={() => {
          toast.dismiss(t.id);
          if (url && url !== "/") {
            navigate(url);
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toast.dismiss(t.id);
            if (url && url !== "/") navigate(url);
          }
        }}
        className={
          "max-w-sm w-full bg-white shadow-lg rounded-xl pointer-events-auto " +
          "flex flex-col gap-1 p-3 cursor-pointer border-l-4 hover:bg-gray-50 transition-colors " +
          (t.visible ? "animate-enter" : "animate-leave")
        }
        style={{ borderLeftColor: accent }}
      >
        <div
          className="text-[10px] font-bold uppercase tracking-widest"
          style={{ color: accent }}
        >
          {kicker}
        </div>
        <div className="text-sm font-semibold text-gray-900 leading-snug">
          {title}
        </div>
        {body ? (
          <div className="text-xs text-gray-600 leading-snug">{body}</div>
        ) : null}
        {url && url !== "/" ? (
          <div className="text-[11px] text-gray-400 mt-0.5">Tap to open</div>
        ) : null}
      </div>
    ),
    {
      duration: 6000,
      position: "top-right",
    }
  );
}
